import type { ReactNode } from 'react';

import type { RuntimeConfig } from './config.js';
import { SessionProvider, useSession } from './session.js';
import { AuthScreens } from './ui/auth-screens.js';
import { Reader } from './ui/reader.js';
import { Loading } from './ui/states.js';

/**
 * The application (M1.14.1).
 *
 * ## There is no router, and that is the choice rather than an omission
 *
 * M1.14.1 asks for routing. What this application has is three states —
 * loading, signed out, signed in — and the signed-in state is a single screen
 * whose selection is *not* a URL: `/items/01JD8Z.42` would be a link to an
 * item somebody else cannot open, in a private alpha with no sharing, and
 * every deep link would need the timeline it was selected from to make sense.
 *
 * So the address bar carries nothing, and a reload lands on the top of the
 * unread list, which is where a reader wants to be anyway. The moment that
 * stops being true — shared links, a settings page, per-feed URLs worth
 * bookmarking — `react-router` is already a dependency and this becomes a
 * `<Routes>` block. Adding it now would be three files of indirection around
 * one screen.
 */

export function App({ config }: { config: RuntimeConfig }): ReactNode {
  return (
    <SessionProvider config={config}>
      <Gate />
    </SessionProvider>
  );
}

function Gate(): ReactNode {
  const session = useSession();

  if (session.status === 'loading') {
    /*
     * Shown while the stored refresh token is exchanged. Brief, and worth a
     * real state rather than a flash of the sign-in form — a signed-in user
     * seeing "Sign in" for 200ms every load reads as a broken session.
     */
    return <Loading label="Signing you in…" />;
  }

  return session.status === 'signed-in' ? <Reader /> : <AuthScreens />;
}
